import { apiRequest } from "./http";

export interface DashboardPayment {
    payment_id: string;
    date: string;
    status: string;
    price: number;
}

export interface DashboardSubscription {
    id: number;
    user_id: number;
    plan_name: string;
    price: number;
    status: string;
    payment_status: string;
    payment_reference: string;
    starts_at: string;
    ends_at: string;
    created_at: string;
    updated_at: string;
    cancelled_at: string | null;
}

export interface DashboardUserMeta {
    phone?: number;
    role?: string;
    display_name?: string;
}

export interface DashboardData {
    firstname: string;
    lastname: string;
    email: string;
    plan_id: number | null;
    phone?: string | null;
    address?: string | null;
    city?: string | null;
    postal_code?: string | null;
    country?: string | null;
    user_meta?: DashboardUserMeta;
    payments: DashboardPayment[];
    subscriptions: DashboardSubscription[];
}

export interface DashboardResponse {
    success: boolean;
    message: string;
    data: DashboardData;
}

const DASHBOARD_CACHE_MS = 1500;
let dashboardCache:
    | {
          token: string;
          expiresAt: number;
          response: DashboardResponse;
      }
    | null = null;
let pendingDashboardRequest:
    | {
          token: string;
          promise: Promise<DashboardResponse>;
      }
    | null = null;

export interface UpdateProfilePayload {
    firstname: string;
    lastname: string;
    display_name: string;
}

export interface UpdateProfileResponse {
    success: boolean;
    message: string;
    data?: {
        firstname?: string;
        lastname?: string;
        user_meta?: {
            display_name?: string;
        };
    };
}

export function clearUserDashboardCache(): void {
    dashboardCache = null;
    pendingDashboardRequest = null;
}

export function getUserDashboard(token: string, options: { force?: boolean } = {}) {
    const now = Date.now();

    if (!options.force && dashboardCache?.token === token && dashboardCache.expiresAt > now) {
        return Promise.resolve(dashboardCache.response);
    }

    if (!options.force && pendingDashboardRequest?.token === token) {
        return pendingDashboardRequest.promise;
    }

    const promise = apiRequest<DashboardResponse>({
        endpoint: "/user/dashboard",
        method: "GET",
        headers: {
            Authorization: `Bearer ${token}`,
        },
    }).then((response) => {
        dashboardCache = {
            token,
            expiresAt: Date.now() + DASHBOARD_CACHE_MS,
            response,
        };

        return response;
    }).finally(() => {
        if (pendingDashboardRequest?.promise === promise) {
            pendingDashboardRequest = null;
        }
    });

    pendingDashboardRequest = { token, promise };

    return promise;
}

export function updateUserProfile(token: string, payload: UpdateProfilePayload) {
    return apiRequest<UpdateProfileResponse>({
        endpoint: "/user/profile",
        method: "PUT",
        headers: {
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
    }).then((response) => {
        clearUserDashboardCache();
        return response;
    });
}

export interface ChangePasswordPayload {
    old_password?: string;
    new_password: string;
}

export interface ChangePasswordResponse {
    success: boolean;
    message: string;
}

export function changeUserPassword(token: string, payload: ChangePasswordPayload) {
    return apiRequest<ChangePasswordResponse>({
        endpoint: "/auth/change-password",
        method: "POST",
        headers: {
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
    });
}
